import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, ShoppingCart, Ticket, DollarSign, Activity, TrendingUp, TrendingDown,
  Package, CheckCircle2, Clock, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { displayReqNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  "Pending Approval": "#F59E0B",
  "Approved":         "#0F9B7F",
  "Fulfilled":        "#0033A0",
  "In Progress":      "#0074B7",
  "Rejected":         "#DA291C",
  "Submitted":        "#94a3b8",
};

const CAT_COLORS = ["#0033A0", "#DA291C", "#0F9B7F", "#F59E0B", "#7C3AED", "#0074B7"];

function Kpi({ label, value, sub, icon: Icon, accent }) {
  return (
    <Card
      data-testid={`my-reports-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

function fmtUsd(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function MyReports() {
  const { persona, userEmail } = usePersona();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    endpoints.myReports(persona.id, userEmail).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [persona.id, userEmail]);

  const trendData = useMemo(() => {
    if (!data?.monthly_orders) return [];
    return data.monthly_orders.map((m) => ({ ...m, label: m.month.slice(5) }));
  }, [data]);

  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="my-reports-page">
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="my-reports-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <BarChart3 size={12} className="text-[#0033A0]" /> Your Reports · Personal View
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">
            Your Flexspace activity, at a glance
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Orders you&apos;ve placed, tickets you&apos;ve raised, devices assigned to you and how they&apos;re performing.
            Everything below is scoped to <span className="font-semibold text-slate-900">{persona.name}</span>.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="My orders" value={k.orders} icon={ShoppingCart} accent sub={`${fmtUsd(k.spent)} approved`} />
        <Kpi label="Open tickets" value={k.open_tickets} icon={Ticket} sub="Active ITSM cases" />
        <Kpi label="My devices" value={k.devices} icon={Package} sub="Assigned to you" />
        <Kpi label="Avg Device UX" value={k.avg_dex} icon={Activity} sub="Across your devices" />
      </div>

      {/* Trend + Status */}
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <TrendingUp size={12} className="text-[#0033A0]" /> Orders · last 6 months
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Your ordering activity</h2>
          <div className="h-64 mt-4" data-testid="my-orders-trend">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#0033A0" strokeWidth={2.5} dot={{ r: 4, fill: "#0033A0" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <ShoppingCart size={12} className="text-[#0033A0]" /> Order status
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Where your requests stand</h2>
          <div className="h-64 mt-4" data-testid="my-orders-status">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.order_status_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.order_status_breakdown.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || CAT_COLORS[i % CAT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 text-[10px]">
            {data.order_status_breakdown.map((s) => (
              <div key={s.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.name] || "#94a3b8" }} />
                <span className="text-slate-600 truncate">{s.name}</span>
                <span className="text-slate-900 font-mono font-semibold ml-auto">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Tickets by category (device DEX moved to MyPC for a single-device view) */}
      <div>
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Ticket size={12} className="text-[#0033A0]" /> Your tickets · by category
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">What are you asking IT?</h2>
          <div className="h-64 mt-4" data-testid="my-ticket-categories">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ticket_category_breakdown} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#0f172a" }} width={130} />
                <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="value" fill="#0033A0" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <Link to="/aipc" className="inline-block mt-3">
            <Button variant="outline" size="sm" className="border-slate-300" data-testid="my-reports-goto-mypc">
              See your device health on MyPC <ArrowRight size={13} className="ml-1" />
            </Button>
          </Link>
        </Card>
      </div>

      {/* Recent orders + tickets */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Clock size={12} className="text-[#0033A0]" /> Recent orders
          </div>
          <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">REQ</th>
                  <th className="text-left px-3 py-2 font-semibold">Device</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500 text-xs">No orders yet.</td></tr>
                )}
                {data.orders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs text-[#0033A0]">{displayReqNumber(o)}</td>
                    <td className="px-3 py-2 text-xs text-slate-900 truncate max-w-[180px]">{o.device_name}</td>
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{fmtUsd(o.total_amount)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[9px]" style={{ borderColor: STATUS_COLORS[o.status] || "#94a3b8", color: STATUS_COLORS[o.status] || "#64748b" }}>{o.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Ticket size={12} className="text-[#0033A0]" /> Recent tickets
          </div>
          <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Ticket</th>
                  <th className="text-left px-3 py-2 font-semibold">Subject</th>
                  <th className="text-left px-3 py-2 font-semibold">Category</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.tickets.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-500 text-xs">No tickets yet.</td></tr>
                )}
                {data.tickets.map((t) => (
                  <tr key={t.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs text-[#0033A0]">
                      <Link to={`/tickets/${t.id}`} className="hover:underline">{displayReqNumber(t)}</Link>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-900 truncate max-w-[180px]">{t.subject}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{t.category}</td>
                    <td className="px-3 py-2 text-[10px]">
                      <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-600">{t.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
